/**
 * Polling Manager
 * Manages health polling for Gateway service
 *
 * ⚠️ SECURITY: Polling uses the same authorization as other Gateway requests.
 * See /SECURITY_GUIDELINES.md for security requirements.
 *
 * State Management:
 * - Updates useGatewayStore for centralized state
 * - Components subscribe to store changes to react to health updates
 */

import { GATEWAY } from '../app-constants';
import { logger } from '../logger';
import { useGatewayStore } from '../../stores/gateway-store';
import { GatewayError, GatewayErrorCode } from './error-codes';
import type {
  ApiResponse,
  HealthState,
  ConnectionState,
  PollStatusInfo,
} from './types';

export interface PollingManagerOptions {
  pollingInterval?: number;
  maxConsecutiveFailures?: number;
  rateLimitBackoffMs?: number;
  statusProvider: () => Promise<ApiResponse>;
  isInitialized: () => boolean;
}

export class PollingManager {
  private pollingInterval: number;
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private isPolling: boolean = false;
  /** Hidden tab (#110): no new timer, also not from a poll still in flight. */
  private isPaused: boolean = false;
  private lastPollTime: number | null = null;
  private lastPollStatus: PollStatusInfo | null = null;
  private consecutiveFailures: number = 0;
  private maxConsecutiveFailures: number;
  private isRateLimited: boolean = false;
  private rateLimitBackoffMs: number;
  /** The poll in flight; tests await it. */
  private currentPollPromise: Promise<void> | null = null;

  private statusProvider: () => Promise<ApiResponse>;
  private isInitialized: () => boolean;

  constructor(options: PollingManagerOptions) {
    this.pollingInterval =
      options.pollingInterval || GATEWAY.DEFAULT_POLLING_INTERVAL_MS;
    this.maxConsecutiveFailures =
      options.maxConsecutiveFailures || GATEWAY.MAX_CONSECUTIVE_FAILURES;
    this.rateLimitBackoffMs = options.rateLimitBackoffMs || 300000;
    this.statusProvider = options.statusProvider;
    this.isInitialized = options.isInitialized;
  }

  /**
   * Start polling gateway status
   * @param interval - Polling interval in milliseconds (optional, uses default if not provided)
   */
  startPolling(interval: number | null = null): void {
    if (interval !== null) {
      this.pollingInterval = interval;
    }

    if (!this.isInitialized()) {
      logger.warn('Cannot start polling: Gateway service not initialized');
      return;
    }

    if (this.isPolling) {
      logger.debug('Polling already active');
      return;
    }

    this.isPolling = true;
    this.isPaused = false;
    this.consecutiveFailures = 0;
    logger.info(
      `Starting gateway status polling (interval: ${this.pollingInterval}ms)`
    );

    // Update store
    useGatewayStore.getState().setPolling(true);
    useGatewayStore.getState().resetFailures();

    // Do first poll immediately and store the promise
    this.currentPollPromise = this.pollStatus();
  }

  /**
   * Stop polling gateway status
   */
  stopPolling(): void {
    logger.info('Stopping gateway status polling');
    this.isPolling = false;

    // Update store
    useGatewayStore.getState().setPolling(false);

    // Always clear timer to prevent leaks
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  /**
   * Pause polling temporarily (keeps isPolling true). A poll already in flight
   * finishes but schedules no next one until resumePolling().
   */
  pausePolling(): void {
    this.isPaused = true;
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  /**
   * Resume polling if it was active. `immediate` polls now (a tab coming back
   * into view, #110) instead of waiting one interval.
   */
  resumePolling(options: { immediate?: boolean } = {}): void {
    this.isPaused = false;
    if (!this.isPolling || this.pollingTimer) return;
    if (options.immediate) {
      this.currentPollPromise = this.pollStatus();
    } else {
      this.scheduleNextPoll();
    }
  }

  /**
   * Handle rate limiting by setting backoff
   */
  handleRateLimit(): void {
    if (this.isRateLimited) {
      return; // Already handling rate limit
    }

    this.isRateLimited = true;
    logger.warn(
      `Gateway rate limit detected. Backing off for ${this.rateLimitBackoffMs / 1000}s`
    );

    // Update store
    useGatewayStore.getState().setRateLimited(true);

    // Schedule rate limit reset
    setTimeout(() => {
      this.isRateLimited = false;
      useGatewayStore.getState().setRateLimited(false);
      logger.info(
        'Rate limit backoff period ended. Resuming normal operations.'
      );
    }, this.rateLimitBackoffMs);
  }

  /**
   * Internal method to perform status poll
   */
  private async pollStatus(): Promise<void> {
    if (!this.isPolling) {
      return;
    }

    // Skip polling if rate limited
    if (this.isRateLimited) {
      logger.debug('Skipping poll - rate limited');
      this.scheduleNextPoll();
      return;
    }

    try {
      const startTime = Date.now();
      const status = await this.statusProvider(); // Get general status
      const responseTime = Date.now() - startTime;

      this.lastPollTime = Date.now();
      this.lastPollStatus = {
        success: true,
        status: status,
        responseTime: responseTime,
        timestamp: this.lastPollTime,
      };

      // Reset failure counter on success
      this.consecutiveFailures = 0;

      // Emit health state based on response
      this.emitHealthState('healthy', this.lastPollStatus);

      logger.debug(`Gateway poll successful (${responseTime}ms)`, status);
    } catch (error) {
      // Don't count rate limit errors as consecutive failures
      const isRateLimitError =
        error instanceof GatewayError &&
        error.code === GatewayErrorCode.RATE_LIMITED;

      if (!isRateLimitError) {
        this.consecutiveFailures++;

        // Update store with failure count
        useGatewayStore.getState().incrementFailures();

        this.lastPollTime = Date.now();
        this.lastPollStatus = {
          success: false,
          error: (error as Error).message,
          timestamp: this.lastPollTime,
        };

        logger.error('Gateway poll failed:', error);

        // Determine health state based on failure count
        if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
          this.emitHealthState('unhealthy', this.lastPollStatus);
        } else {
          this.emitHealthState('degraded', this.lastPollStatus);
        }
      }
      // For rate limit errors, just silently skip (already logged the warning)
    }

    // Schedule next poll (moved outside finally to ensure it happens after error handling)
    this.scheduleNextPoll();
  }

  /**
   * Schedule the next status poll
   */
  private scheduleNextPoll(): void {
    if (!this.isPolling || this.isPaused) {
      return;
    }

    this.pollingTimer = setTimeout(() => {
      this.currentPollPromise = this.pollStatus();
    }, this.pollingInterval);
  }

  /**
   * Emit health state change
   * Updates the centralized Zustand store - subscribers react to changes
   * @param health - Health state: 'healthy', 'degraded', 'unhealthy'
   * @param details - Health check details
   */
  private emitHealthState(health: HealthState, details: PollStatusInfo): void {
    logger.info(`Gateway health state changed: ${health}`);

    // Update centralized store - subscribers will react to this change
    useGatewayStore.getState().setHealth(health, details);

    // Update connection state based on health
    // healthy/degraded = connected (still reachable), unhealthy = disconnected
    const connectionState: ConnectionState =
      health === 'unhealthy' ? 'disconnected' : 'connected';
    this.emitConnectionState(connectionState);
  }

  /**
   * Emit connection state change
   * Updates the centralized Zustand store - subscribers react to changes
   * @param state - Connection state: 'connected' or 'disconnected'
   */
  emitConnectionState(state: ConnectionState): void {
    logger.info(`Gateway connection state changed: ${state}`);

    // Update centralized store - subscribers will react to this change
    useGatewayStore.getState().setConnection(state);
  }
}
